import { useCallback } from 'react';
import { useFeatureMutation } from '@common/hooks/query/useFeatureMutation';
import { useStartCarrickWorkflowMutation } from '../api';
import { SendSequenceArgs, StartCarrickWorkflowArgs } from '../types';
import { useSelectedProductionId } from '@features/productions';

export const useSendAction = () => {
    const productionId = useSelectedProductionId();

    const { execute: triggerCarrickApi, ...mutationState } = useFeatureMutation(
        useStartCarrickWorkflowMutation,
    );

    const executeSend = useCallback(
        async ({
            requestorEmail,
            items,
            subject,
            recipients,
            ccEmails,
        }: SendSequenceArgs): Promise<string> => {
            if (!productionId) {
                throw new Error(
                    'Cannot execute send: No production ID found in context.',
                );
            }
            const numericProdId = parseInt(productionId, 10);

            const payload: StartCarrickWorkflowArgs = {
                name: 'Customer Servicing',
                requestor: requestorEmail,
                parameters: {
                    outputAssets: [],
                    inputAssets: items.map(({ name, id }) => ({
                        name: name,
                        customerRef: `${productionId}/${id}`,
                        privateInfo: { productionId: numericProdId },
                    })),
                    globalParams: [
                        { name: 'action', value: 'Send File' },
                        { name: 'targetResource', value: 'FASPEX2_ECLAIR' },
                        { name: 'sentObject', value: subject },
                        {
                            name: 'mailtoClient',
                            value: recipients.join(','),
                        },
                        ...(ccEmails && ccEmails.length > 0
                            ? [
                                  {
                                      name: 'ccClient',
                                      value: ccEmails.join(','),
                                  },
                              ]
                            : []),
                    ],
                },
            };

            return await triggerCarrickApi(payload);
        },
        [productionId, triggerCarrickApi],
    );

    return { executeSend, ...mutationState };
};
